import React, { useState } from "react";
import { Server, LineChart, KeyRound, Volume2, Trash2, Eye, EyeOff, Lock, ShieldAlert, Plus, X } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { useAuth } from "../../context/AuthContext";
import { Panel, Overline } from "../common/Panel";

const ADMIN_EMAILS = [
  "hhnk3693@gmail.com",
  "henockhnk22@gmail.com",
  "hhnk3693@gmail.com",
  "admin@test.com",
];

const inputCls = "w-full bg-[var(--surface2)] border border-[var(--bd)] rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-[var(--accent)] transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-[var(--t2)]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function MaskedField({ label, value, testid, onChange, placeholder }) {
  return (
    <Field label={label}>
      <input
        data-testid={testid}
        className={inputCls}
        type="password"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
    </Field>
  );
}

function RevealField({ label, value, testid, onChange, placeholder, visible }) {
  return (
    <Field label={label}>
      <input
        data-testid={testid}
        className={inputCls}
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
    </Field>
  );
}

const VOICE_ENGINES = [
  ["elevenlabs", "ElevenLabs ★★★★★"],
  ["azure", "Azure Neural TTS ★★★★"],
  ["streamelements", "StreamElements ★★★"],
  ["voicerss", "VoiceRSS ★★"],
  ["browser", "Browser TTS ★★"],
];

function mask(key) {
  if (!key || key.length < 8) return "••••••••";
  return key.slice(0, 4) + "••••••" + key.slice(-4);
}

export default function SettingsTab() {
  const { settings: s, updateSettings, resetSettings, reconnect } = useApp();
  const { user } = useAuth();
  const [keysVisible, setKeysVisible] = useState(false);
  const [newGemKey, setNewGemKey] = useState("");
  const [newDsKey, setNewDsKey] = useState("");

  const isAdmin = ADMIN_EMAILS.includes(user?.email);
  const gemKeys = (s.geminiKeys || "").split("\n").map((k) => k.trim()).filter(Boolean);
  const geminiCount = gemKeys.length;

  const addGemKey = () => {
    const k = newGemKey.trim();
    if (!k) return;
    if (gemKeys.includes(k)) return;
    updateSettings({ geminiKeys: [...gemKeys, k].join("\n") });
    setNewGemKey("");
  };
  const removeGemKey = (idx) => {
    const updated = gemKeys.filter((_, i) => i !== idx);
    updateSettings({ geminiKeys: updated.join("\n") });
  };

  return (
    <div className="space-y-6">
      <div>
        <Overline>Configuration</Overline>
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Settings</h2>
      </div>

      {/* MQTT */}
      <Panel className="p-6 space-y-4">
        <div className="flex items-center gap-2"><Server size={16} className="text-[var(--accent)]" /><Overline>MQTT Broker</Overline></div>
        <Field label="Host"><input data-testid="set-mqtt-host" className={inputCls} value={s.mqttHost} onChange={(e) => updateSettings({ mqttHost: e.target.value })} /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Username"><input data-testid="set-mqtt-user" className={inputCls} value={s.mqttUser} onChange={(e) => updateSettings({ mqttUser: e.target.value })} /></Field>
          <MaskedField label="Password" value={s.mqttPass} testid="set-mqtt-pass" onChange={(e) => updateSettings({ mqttPass: e.target.value })} placeholder="password" />
        </div>
        <button data-testid="set-mqtt-save" onClick={() => reconnect(s.mqttHost, s.mqttUser, s.mqttPass)} className="px-4 py-2 rounded-md bg-[var(--accent)] text-black text-sm font-medium active:scale-95 transition-transform">Save & Reconnect</button>
      </Panel>

      {/* Grafana */}
      <Panel className="p-6 space-y-4">
        <div className="flex items-center gap-2"><LineChart size={16} className="text-[var(--accent)]" /><Overline>Grafana</Overline></div>
        <Field label="Dashboard URL"><input data-testid="set-grafana-url" className={inputCls} value={s.grafanaUrl} onChange={(e) => updateSettings({ grafanaUrl: e.target.value })} /></Field>
      </Panel>

      {/* AI Keys */}
      <Panel className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><KeyRound size={16} className="text-[var(--accent)]" /><Overline>AI Keys</Overline></div>
          {!isAdmin && (
            <span className="flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-md border border-[var(--bd)] text-[var(--t2)] cursor-not-allowed opacity-60">
              <ShieldAlert size={13} /> Admin only
            </span>
          )}
        </div>

        {!isAdmin && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-[var(--surface2)] border border-[var(--bd)] text-xs font-mono text-[var(--t2)]">
            <Lock size={13} className="shrink-0 text-[var(--danger)]" />
            API keys are restricted to the system administrator.
          </div>
        )}

        {isAdmin && (
          <>
            <div>
              <span className="text-xs uppercase tracking-wider text-[var(--t2)]">Gemini API Keys - {geminiCount} configured</span>
              <p className="text-[11px] text-[var(--t2)] mt-1">Keys rotate automatically on 429 rate limits. Free 15 req/min per key.</p>
            </div>

            {gemKeys.length > 0 && (
              <div className="space-y-1.5">
                {gemKeys.map((k, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-md bg-[var(--surface2)] border border-[var(--bd)]">
                    <span className="text-xs font-mono text-[var(--t2)] mr-1">#{i + 1}</span>
                    <span className="flex-1 text-sm font-mono">{mask(k)}</span>
                    <button onClick={() => removeGemKey(i)} className="w-6 h-6 rounded flex items-center justify-center text-[var(--danger)] hover:bg-[var(--danger)]/10 transition-colors" title="Remove key">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <input
                data-testid="set-gemini-add"
                className={inputCls}
                type="password"
                value={newGemKey}
                onChange={(e) => setNewGemKey(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addGemKey()}
                placeholder="Paste new Gemini key (AIzaSy...)"
              />
              <button onClick={addGemKey} className="shrink-0 px-3 py-2 rounded-md bg-[var(--accent)] text-black text-sm font-medium flex items-center gap-1.5 active:scale-95 transition-transform">
                <Plus size={14} /> Add
              </button>
            </div>

            <div className="border-t border-[var(--bd)] pt-4 mt-2">
              <span className="text-xs uppercase tracking-wider text-[var(--t2)]">DeepSeek Fallback Key</span>
              <p className="text-[11px] text-[var(--t2)] mt-1 mb-2">Used as last resort when all Gemini keys are rate-limited.</p>
              <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[var(--surface2)] border border-[var(--bd)]">
                <span className="flex-1 text-sm font-mono">{s.deepseekKey ? mask(s.deepseekKey) : "Not set"}</span>
              </div>
              <input
                data-testid="set-deepseek-key"
                className={inputCls + " mt-2"}
                type="password"
                value={newDsKey}
                onChange={(e) => setNewDsKey(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && newDsKey.trim()) { updateSettings({ deepseekKey: newDsKey.trim() }); setNewDsKey(""); } }}
                placeholder="Paste DeepSeek key to replace (sk-...)"
              />
              {newDsKey.trim() && (
                <button onClick={() => { updateSettings({ deepseekKey: newDsKey.trim() }); setNewDsKey(""); }} className="mt-2 px-3 py-2 rounded-md bg-[var(--accent)] text-black text-sm font-medium active:scale-95 transition-transform">
                  Save DeepSeek Key
                </button>
              )}
            </div>
          </>
        )}
      </Panel>

      {/* Voice Engine */}
      <Panel className="p-6 space-y-4">
        <div className="flex items-center gap-2"><Volume2 size={16} className="text-[var(--accent)]" /><Overline>AI Voice Engine</Overline></div>
        <Field label="Provider">
          <div className="space-y-1.5" data-testid="set-voice-engine">
            {VOICE_ENGINES.map(([v, l]) => (
              <button
                key={v}
                onClick={() => updateSettings({ voiceEngine: v })}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md border text-sm font-mono text-left transition-colors ${
                  s.voiceEngine === v
                    ? "border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--accent)]"
                    : "border-[var(--bd)] bg-[var(--surface2)] text-[var(--t1)] hover:border-[var(--bd-strong)]"
                }`}
              >
                <span className={`w-4 h-4 shrink-0 rounded-full border-2 flex items-center justify-center ${
                  s.voiceEngine === v ? "border-[var(--accent)]" : "border-[var(--t2)]"
                }`}>
                  {s.voiceEngine === v && <span className="w-2 h-2 rounded-full bg-[var(--accent)]" />}
                </span>
                {l}
              </button>
            ))}
          </div>
        </Field>

        {s.voiceEngine === "elevenlabs" && (
          <div className="space-y-4">
            {isAdmin
              ? <MaskedField label="ElevenLabs API Key" value={s.elevenKey} testid="set-eleven-key" onChange={(e) => updateSettings({ elevenKey: e.target.value })} placeholder="sk_..." />
              : <div className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-[var(--surface2)] border border-[var(--bd)] text-xs font-mono text-[var(--t2)]"><Lock size={13} className="text-[var(--danger)]" />ElevenLabs key - admin only</div>
            }
            <Field label="Voice ID">
              <input data-testid="set-eleven-voice" className={inputCls} value={s.elevenVoiceId} onChange={(e) => updateSettings({ elevenVoiceId: e.target.value })} placeholder="21m00Tcm4TlvDq8ikWAM" />
            </Field>
            <p className="text-[11px] text-[var(--t2)] font-mono">Get Voice IDs at elevenlabs.io → Voices. Default is Rachel (English).</p>
          </div>
        )}
        {s.voiceEngine === "azure" && (
          <div className="space-y-4">
            {isAdmin
              ? <MaskedField label="Azure Key" value={s.azureKey} testid="set-azure-key" onChange={(e) => updateSettings({ azureKey: e.target.value })} placeholder="Your Azure Key 1" />
              : <div className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-[var(--surface2)] border border-[var(--bd)] text-xs font-mono text-[var(--t2)]"><Lock size={13} className="text-[var(--danger)]" />Azure key - admin only</div>
            }
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Region"><input data-testid="set-azure-region" className={inputCls} value={s.azureRegion} onChange={(e) => updateSettings({ azureRegion: e.target.value })} placeholder="southafricanorth" /></Field>
              <Field label="Voice Name"><input data-testid="set-azure-voice" className={inputCls} value={s.azureVoice} onChange={(e) => updateSettings({ azureVoice: e.target.value })} placeholder="en-US-JennyNeural" /></Field>
            </div>
            <p className="text-[11px] text-[var(--t2)] font-mono">Voice names: en-US-JennyNeural · en-ZA-LeahNeural · en-GB-LibbyNeural</p>
          </div>
        )}
        {s.voiceEngine === "voicerss" && (
          <div className="space-y-4">
            {isAdmin
              ? <MaskedField label="VoiceRSS Key" value={s.voicerssKey} testid="set-voicerss-key" onChange={(e) => updateSettings({ voicerssKey: e.target.value })} placeholder="your-key" />
              : <div className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-[var(--surface2)] border border-[var(--bd)] text-xs font-mono text-[var(--t2)]"><Lock size={13} className="text-[var(--danger)]" />VoiceRSS key - admin only</div>
            }
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Language Code"><input data-testid="set-voicerss-lang" className={inputCls} value={s.voicerssLang} onChange={(e) => updateSettings({ voicerssLang: e.target.value })} placeholder="en-us" /></Field>
              <Field label="Voice Name"><input data-testid="set-voicerss-voice" className={inputCls} value={s.voicerssVoice} onChange={(e) => updateSettings({ voicerssVoice: e.target.value })} placeholder="Linda" /></Field>
            </div>
          </div>
        )}
        {s.voiceEngine === "streamelements" && <p className="text-xs text-[var(--t2)] font-mono">StreamElements requires no API key - free public TTS endpoint.</p>}
        {s.voiceEngine === "browser" && <p className="text-xs text-[var(--t2)] font-mono">Uses the built-in Web Speech Synthesis API. No key required.</p>}

        <Field label={`Speech Rate - ${s.speechRate.toFixed(1)}×`}>
          <input data-testid="set-speech-rate" type="range" min="0.5" max="2" step="0.1" value={s.speechRate} onChange={(e) => updateSettings({ speechRate: parseFloat(e.target.value) })} className="w-full accent-[var(--accent)]" />
        </Field>
      </Panel>

      {/* Danger Zone */}
      <Panel className="p-6 border-[var(--danger)]/40">
        <Overline className="text-[var(--danger)]">Danger Zone</Overline>
        <p className="text-sm text-[var(--t2)] mt-2 mb-3">Permanently reset all configuration to factory defaults.</p>
        <button data-testid="set-reset-all" onClick={resetSettings} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[var(--danger)]/50 text-[var(--danger)] text-sm font-medium hover:bg-[var(--danger)]/10 transition-colors">
          <Trash2 size={15} /> Reset All Configuration
        </button>
      </Panel>
    </div>
  );
}
